import React, { useState, useCallback, useEffect } from 'react';
import { apiClient } from '../../../api/client';
import {
  TEXT_DARK, TEXT_MUTED,
  FONT_HEADING, PRIMARY, BORDER_LIGHT,
} from './designTokens';

interface CheckInGuest {
  id: string;
  fullName: string;
  ticketNumber: string | null;
  seatId: string | null;
  seatCompositeId: string | null;
  tableNumber: number | null;
  seatNumber: number | null;
  checkedIn: boolean;
  checkedInAt: string | null;
}

interface CheckInCounts {
  checkedIn: number;
  totalAssigned: number;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '700px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.1rem',
    fontFamily: FONT_HEADING,
    fontWeight: 700,
    color: TEXT_DARK,
    margin: 0,
  },
  counts: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.4rem 0.75rem',
    backgroundColor: '#f0f7f4',
    borderRadius: '4px',
    border: `1px solid ${BORDER_LIGHT}`,
  },
  countValue: {
    fontSize: '1rem',
    fontWeight: 700,
    color: PRIMARY,
  },
  countLabel: {
    fontSize: '0.75rem',
    color: TEXT_MUTED,
  },
  searchSection: {
    marginBottom: '1rem',
  },
  searchRow: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '0.5rem',
  },
  input: {
    flex: 1,
    padding: '0.55rem 0.75rem',
    fontSize: '0.85rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    outline: 'none',
  },
  searchBtn: {
    padding: '0.55rem 1rem',
    fontSize: '0.82rem',
    fontWeight: 600,
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  searchTypeToggle: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '0.75rem',
  },
  toggleBtn: {
    padding: '0.35rem 0.7rem',
    fontSize: '0.78rem',
    fontWeight: 500,
    border: '1px solid #ccc',
    borderRadius: '4px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    color: '#555',
  },
  toggleBtnActive: {
    padding: '0.35rem 0.7rem',
    fontSize: '0.78rem',
    fontWeight: 600,
    border: `1px solid ${PRIMARY}`,
    borderRadius: '4px',
    backgroundColor: '#e8f5e9',
    cursor: 'pointer',
    color: PRIMARY,
  },
  results: {
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  resultItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.65rem 0.85rem',
    borderBottom: '1px solid #f0f0f0',
    transition: 'background-color 0.1s',
  },
  guestInfo: {
    flex: 1,
  },
  guestName: {
    fontSize: '0.88rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  guestMeta: {
    fontSize: '0.75rem',
    color: TEXT_MUTED,
    marginTop: '0.15rem',
  },
  checkInBtn: {
    padding: '0.4rem 0.85rem',
    fontSize: '0.8rem',
    fontWeight: 600,
    backgroundColor: '#2e7d32',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  reverseBtn: {
    padding: '0.4rem 0.85rem',
    fontSize: '0.8rem',
    fontWeight: 600,
    backgroundColor: '#f57c00',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  checkedInBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    fontSize: '0.72rem',
    fontWeight: 600,
    color: '#6a1b9a',
    backgroundColor: '#f3e5f5',
    padding: '0.2rem 0.5rem',
    borderRadius: '3px',
    marginRight: '0.5rem',
  },
  noResults: {
    padding: '1.5rem',
    textAlign: 'center' as const,
    color: '#888',
    fontSize: '0.85rem',
  },
  loading: {
    padding: '1.5rem',
    textAlign: 'center' as const,
    color: '#666',
    fontSize: '0.85rem',
  },
  error: {
    padding: '0.5rem 0.75rem',
    color: '#d32f2f',
    fontSize: '0.8rem',
    backgroundColor: '#fdecea',
    borderRadius: '4px',
    marginBottom: '0.75rem',
  },
  success: {
    padding: '0.5rem 0.75rem',
    color: '#2e7d32',
    fontSize: '0.8rem',
    backgroundColor: '#e8f5e9',
    borderRadius: '4px',
    marginBottom: '0.75rem',
  },
};

export function CheckInPanel() {
  const [searchType, setSearchType] = useState<'name' | 'ticket' | 'order'>('name');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CheckInGuest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [counts, setCounts] = useState<CheckInCounts | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const fetchCounts = useCallback(async () => {
    try {
      const data = await apiClient<CheckInCounts>('/table-management/check-in/counts');
      setCounts(data);
    } catch {
      // Silently fail for counts
    }
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const search = useCallback(async () => {
    if (!query.trim()) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const params = new URLSearchParams();
      if (searchType === 'name') {
        params.set('name', query.trim());
      } else if (searchType === 'order') {
        params.set('orderNumber', query.trim());
      } else {
        params.set('ticketNumber', query.trim());
      }
      const data = await apiClient<{ results: CheckInGuest[]; totalResults: number }>(
        `/table-management/check-in/search?${params.toString()}`
      );
      setResults(data.results || []);
      setHasSearched(true);
    } catch {
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [query, searchType]);

  const handleCheckIn = async (seatId: string, guestName: string) => {
    setError('');
    setSuccess('');
    try {
      await apiClient('/table-management/check-in', {
        method: 'POST',
        body: { seatId },
      });
      setSuccess(`${guestName} checked in successfully.`);
      // Refresh results and counts
      search();
      fetchCounts();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || 'Check-in failed.');
    }
  };

  const handleReverse = async (seatId: string, guestName: string) => {
    setError('');
    setSuccess('');
    try {
      await apiClient(`/table-management/check-in/${seatId}/reverse`, {
        method: 'POST',
      });
      setSuccess(`Check-in reversed for ${guestName}.`);
      search();
      fetchCounts();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || 'Reversal failed.');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      search();
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Guest Check-In</h2>
        {counts && (
          <div style={styles.counts} role="status" aria-label="Check-in progress">
            <span style={styles.countValue}>
              {counts.checkedIn} / {counts.totalAssigned}
            </span>
            <span style={styles.countLabel}>checked in</span>
          </div>
        )}
      </div>

      <div style={styles.searchSection}>
        <div style={styles.searchTypeToggle} role="radiogroup" aria-label="Search type">
          <button
            style={searchType === 'name' ? styles.toggleBtnActive : styles.toggleBtn}
            onClick={() => setSearchType('name')}
            role="radio"
            aria-checked={searchType === 'name'}
          >
            Search by Name
          </button>
          <button
            style={searchType === 'ticket' ? styles.toggleBtnActive : styles.toggleBtn}
            onClick={() => setSearchType('ticket')}
            role="radio"
            aria-checked={searchType === 'ticket'}
          >
            Search by Ticket #
          </button>
          <button
            style={searchType === 'order' ? styles.toggleBtnActive : styles.toggleBtn}
            onClick={() => setSearchType('order')}
            role="radio"
            aria-checked={searchType === 'order'}
          >
            Search by Order #
          </button>
        </div>

        <div style={styles.searchRow}>
          <input
            style={styles.input}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              searchType === 'name'
                ? 'Enter guest name (partial match)...'
                : searchType === 'order'
                ? 'Enter order number...'
                : 'Enter exact ticket number...'
            }
            aria-label={searchType === 'name' ? 'Search by guest name' : searchType === 'order' ? 'Search by order number' : 'Search by ticket number'}
          />
          <button
            style={styles.searchBtn}
            onClick={search}
            disabled={loading || !query.trim()}
            aria-label="Search guests"
          >
            Search
          </button>
        </div>
      </div>

      {error && <div style={styles.error} role="alert">{error}</div>}
      {success && <div style={styles.success} role="status">{success}</div>}

      {loading && <div style={styles.loading}>Searching...</div>}

      {!loading && hasSearched && results.length === 0 && (
        <div style={styles.noResults}>No guests found matching your search.</div>
      )}

      {!loading && results.length > 0 && (
        <div style={styles.results} role="list" aria-label="Check-in search results">
          {results.map((guest) => (
            <div
              key={guest.id}
              style={{
                ...styles.resultItem,
                backgroundColor: guest.checkedIn ? '#fafafa' : '#fff',
              }}
              role="listitem"
            >
              <div style={styles.guestInfo}>
                <div style={styles.guestName}>{guest.fullName}</div>
                <div style={styles.guestMeta}>
                  {guest.ticketNumber && `Ticket: ${guest.ticketNumber}`}
                  {guest.seatCompositeId && ` • Seat: ${guest.seatCompositeId}`}
                  {guest.tableNumber && ` (Table ${guest.tableNumber})`}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {guest.checkedIn && (
                  <>
                    <span style={styles.checkedInBadge}>
                      ✓ Checked In
                      {guest.checkedInAt && (
                        <span style={{ fontWeight: 400, marginLeft: '0.25rem' }}>
                          {new Date(guest.checkedInAt).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      )}
                    </span>
                    <button
                      style={styles.reverseBtn}
                      onClick={() => handleReverse(guest.seatId!, guest.fullName)}
                      aria-label={`Reverse check-in for ${guest.fullName}`}
                    >
                      Reverse
                    </button>
                  </>
                )}
                {!guest.checkedIn && guest.seatCompositeId && (
                  <button
                    style={styles.checkInBtn}
                    onClick={() => handleCheckIn(guest.seatId!, guest.fullName)}
                    aria-label={`Check in ${guest.fullName}`}
                  >
                    Check In
                  </button>
                )}
                {!guest.checkedIn && !guest.seatCompositeId && (
                  <span style={{ fontSize: '0.75rem', color: '#888', fontStyle: 'italic' }}>
                    No seat assigned
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
